/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment, type FC } from 'react'
import { useIntl } from 'react-intl'
import { groupBy } from 'lodash'

import {
  EuiAvatar,
  EuiFlexGroup,
  EuiHorizontalRule,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  SliderDefinition,
  ZoneSummary as ZoneSummaryType,
  ZoneSummaryInstance,
} from '@modules/ui-types'

import { getSliderDefinition } from '@/lib/sliders/definitions'
import { useSliderInstanceColorPicker } from '@/lib/sliders/colors'

type ZoneSummaryProps = {
  zone: ZoneSummaryType
  zoneIndex: number
}

interface ZoneSummaryWithCount extends ZoneSummaryInstance {
  nodeCount: number
  sliderDefinition: SliderDefinition
}

const ZoneSummary: FC<ZoneSummaryProps> = ({ zone, zoneIndex }) => {
  const getSliderInstanceColor = useSliderInstanceColorPicker()
  const { formatMessage } = useIntl()
  const zoneLabels = [`a`, `b`, `c`]

  const zoneWithDisplayData: ZoneSummaryWithCount[] = Object.values(groupBy(zone, 'id')).map(
    (zoneInstances) => {
      // This must be defined
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const instance = zoneInstances[0]!
      return {
        ...instance,
        nodeCount: zoneInstances.length,
        sliderDefinition: getSliderDefinition({ sliderInstanceType: instance.sliderInstanceType }),
      }
    },
  )

  return (
    <div data-test-id={`zone-summary-${zoneIndex + 1}`}>
      <EuiText css={css({ textTransform: 'capitalize' })} size='s' color='subdued'>
        <FormattedMessage
          id='zone-summary.zone-labe'
          defaultMessage='Zone {zoneLabel}'
          values={{ zoneLabel: zoneLabels[zoneIndex] || `` }}
        />
      </EuiText>

      <EuiHorizontalRule margin='s' />

      <EuiFlexGroup gutterSize='s'>
        {zoneWithDisplayData.map((instance) => (
          <EuiToolTip
            content={
              <Fragment>
                {instance.name} — {instance.size}
                {instance.storage ? [` and `, instance.storage] : ``}
              </Fragment>
            }
            key={instance.id}
          >
            <EuiAvatar
              data-type={instance.type}
              data-test-id={`instance-summary-viz-circle-${instance.id}`}
              aria-label={formatMessage(
                {
                  id: 'zone-summary.zone-aria-label',
                  defaultMessage:
                    '{type} {nodeCount, plural, one {instance} other {# instances}}, {name}',
                },
                {
                  type: formatMessage(instance.sliderDefinition.messages.prettyName),
                  name: instance.name,
                  nodeCount: instance.nodeCount,
                },
              )}
              tabIndex={0}
              name={instance.nodeCount > 1 ? instance.nodeCount.toString() : ``}
              color={getSliderInstanceColor(instance.type)}
              initialsLength={2}
            />
          </EuiToolTip>
        ))}
      </EuiFlexGroup>
      <EuiSpacer size='m' />
    </div>
  )
}

export default ZoneSummary
